import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Link2,
  Copy,
  MessageSquare,
  Mail,
  Share2,
  Check,
  Leaf,
} from "lucide-react";

interface ReferralShareKitProps {
  referralLink: string;
}

/** Show a short display version of a referral URL */
const shortLink = (url: string) => {
  try {
    const u = new URL(url);
    // Extract code from /r/:code path
    const match = u.pathname.match(/^\/r\/(.+)/);
    if (match) {
      return `${u.host}/r/${match[1].slice(0, 8)}`;
    }
    // Fallback for old-style ?ref= links
    const ref = u.searchParams.get("ref") || "";
    return `${u.host}/r/${ref.slice(0, 8)}`;
  } catch {
    return url;
  }
};

const SHARE_MESSAGES = [
  {
    id: "intro",
    label: "Introduction",
    channel: "text",
    text: (link: string) =>
      `Hey! I wanted to share something that's been helping people feel their best — Elara Health offers personalized peptide therapy with board-certified providers. It's all online, convenient, and backed by real science. Check it out: ${link}`,
  },
  {
    id: "wellness",
    label: "Wellness Focus",
    channel: "text",
    text: (link: string) =>
      `Looking to optimize your health? Elara Health connects you with expert providers for personalized peptide therapy — better energy, recovery, and overall wellness. Start here: ${link}`,
  },
  {
    id: "email-warm",
    label: "Warm Email",
    channel: "email",
    subject: "Something I thought you'd find interesting",
    text: (link: string) =>
      `Hi there,\n\nI've been learning about peptide therapy and wanted to pass along Elara Health — they make it easy to get started with personalized treatment plans from board-certified providers, all from the comfort of home.\n\nIf you've been curious about optimizing your health, recovery, or energy levels, this could be a great fit.\n\nLearn more here: ${link}\n\nLet me know if you have any questions!`,
  },
  {
    id: "email-direct",
    label: "Direct Email",
    channel: "email",
    subject: "Personalized peptide therapy — worth a look",
    text: (link: string) =>
      `Hi,\n\nI wanted to share Elara Health with you. They offer FDA-compliant peptide therapy with licensed providers who create personalized plans for weight management, recovery, anti-aging, and more.\n\nThe process is simple: complete a health assessment, consult with a provider, and get your prescription delivered.\n\nGet started: ${link}\n\nBest regards`,
  },
  {
    id: "social",
    label: "Social Post",
    channel: "social",
    text: (link: string) =>
      `Peptide therapy is changing the game for health optimization 🧬 Elara Health makes it accessible with board-certified providers and personalized plans. Worth checking out → ${link}`,
  },
];

const ReferralShareKit = ({ referralLink }: ReferralShareKitProps) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const channelIcon = (channel: string) => {
    switch (channel) {
      case "email": return <Mail size={14} className="text-muted-foreground" />;
      case "social": return <Share2 size={14} className="text-muted-foreground" />;
      default: return <MessageSquare size={14} className="text-muted-foreground" />;
    }
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Share2 size={16} /> Share & Grow
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Branded Referral Card Preview */}
        <div className="rounded-xl overflow-hidden border border-border shadow-sm">
          <div className="bg-primary p-6 text-primary-foreground relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-primary-foreground/5 -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-primary-foreground/5 translate-y-1/2 -translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Leaf size={20} />
                <span className="font-display text-xl tracking-wide">ELARA HEALTH</span>
              </div>
              <p className="text-sm text-primary-foreground/80 leading-relaxed max-w-sm">
                Personalized peptide therapy with board-certified providers. Start your wellness journey today.
              </p>
            </div>
          </div>
          <div className="bg-card p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground mb-1">Your personal referral link</p>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={shortLink(referralLink)}
                  className="text-xs font-mono bg-secondary/50 h-8"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5 h-8"
                  onClick={() => copyToClipboard(referralLink, "link")}
                >
                  {copiedId === "link" ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === "link" ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Pre-written messages */}
        <div>
          <p className="text-sm font-medium text-foreground mb-3">Ready-to-share messages</p>
          <Tabs defaultValue="intro" className="w-full">
            <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-transparent p-0">
              {SHARE_MESSAGES.map((msg) => (
                <TabsTrigger
                  key={msg.id}
                  value={msg.id}
                  className="text-xs gap-1.5 data-[state=active]:bg-secondary data-[state=active]:shadow-none border border-transparent data-[state=active]:border-border rounded-full px-3 py-1.5"
                >
                  {channelIcon(msg.channel)}
                  {msg.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {SHARE_MESSAGES.map((msg) => {
              const fullText = msg.text(referralLink);
              const displayText = msg.text(shortLink(referralLink));
              return (
                <TabsContent key={msg.id} value={msg.id} className="mt-3">
                  <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-3">
                    {"subject" in msg && msg.subject && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground shrink-0">Subject:</span>
                        <span className="text-sm text-foreground font-medium">{msg.subject}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 ml-auto shrink-0"
                          onClick={() => copyToClipboard(msg.subject!, `subject-${msg.id}`)}
                        >
                          {copiedId === `subject-${msg.id}` ? <Check size={10} /> : <Copy size={10} />}
                        </Button>
                      </div>
                    )}
                    <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">
                      {displayText}
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        variant="default"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => copyToClipboard(fullText, msg.id)}
                      >
                        {copiedId === msg.id ? <Check size={12} /> : <Copy size={12} />}
                        {copiedId === msg.id ? "Copied!" : "Copy Message"}
                      </Button>
                      {msg.channel === "email" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => {
                            const subject = "subject" in msg ? msg.subject : "";
                            window.open(
                              `mailto:?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(fullText)}`,
                              "_blank"
                            );
                          }}
                        >
                          <Mail size={12} /> Open in Email
                        </Button>
                      )}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralShareKit;
